'use strict'
import React, { useState, useEffect, useRef, useMemo } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  makeStyles,
  Typography,
  Tooltip,
  IconButton,
  Drawer,
  Link as MuiLink,
  Menu,
  MenuItem
} from '@material-ui/core'
import WarningIcon from '@material-ui/icons/Warning'
import CloseIcon from '@material-ui/icons/Close'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import MapIcon from '@material-ui/icons/Map'
import EditIcon from '@material-ui/icons/Edit'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import { HMI_YARD, formatMinutes } from '../Routing/TerritoryWorkbench/mapUtils'
import { applyBasemapProvider, googleHybridProvider } from '../HerbicideSchedule/v3/maricopaBasemap'
import ProfitabilityPanel from './ProfitabilityPanel'
import ProfitabilitySettingsModal from './ProfitabilitySettingsModal'
import { useProfitabilitySettings } from './useProfitabilitySettings'
import HoursValue from '../Routing/HoursValue'
import { HoursGlossaryButton } from '../Routing/HoursGlossaryPanel'
import { useRouteInterStopDrive } from '../Routing/useRouteInterStopDrive'
import {
  V4_T,
  formatCurrency,
  formatNumber,
  formatPercent,
  formatCurrencyOrPending,
  getTicketLabel,
  getAspireTimeEntryUrl,
  getVisitPeopleChips,
  getVisitOT,
  getVisitTimeRange,
  getVisitAssociatedDriveHours,
  getVisitTrendStats,
  filterVisitRowsByType,
  safeNumber,
  formatCompactCurrency,
  getDayAbbrev,
  getPropertyCoordinates,
  formatMiles,
  getAspirePropertyId,
  getAspireWorkTicketUrl,
  getAspireWorkTicketVisitUrl,
  buildFinancialPeriodData,
  FINANCIAL_PERIOD_OPTIONS,
  formatAddress,
  getRibbonMargin
} from './propertyDetailHelpers'
import { usePropertyDetailController } from './usePropertyDetail'

// Shared constants used by the drawer (duplicated intentionally during the
// Property Detail extraction; the table keeps its own copies until the dead
// drawer block is removed in a later task).

const PROPERTY_DETAIL_TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'history', label: 'History' },
  { value: 'service', label: 'Service' },
  { value: 'schedule', label: 'Schedule' },
  { value: 'financials', label: 'Financials' },
  { value: 'profitability', label: 'Profitability' }
]

const SERVICE_COLORS = {
  Maint: V4_T.orange,
  Maintenance: V4_T.orange,
  Irrig: V4_T.green,
  Irrigation: V4_T.green,
  Herb: V4_T.plum,
  Herbicide: V4_T.plum,
  Other: V4_T.mute
}

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))


// Styles for the Property Detail drawer + its sub-components. Exported so
// controlled callers (RoutingPropertiesTable, RefineRouteTab) can build the
// classes prop they pass in.
export const useRoutingPropertyDetailStyles = makeStyles(theme => ({
  drawer: {
    width: 420,
    maxWidth: '100vw',
    backgroundColor: '#f4efe4',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    color: '#1c2419',
    borderLeft: '1px solid #e7dfc9'
  },
  drawerHeader: {
    padding: '12px 16px 11px',
    backgroundColor: '#fbf8f1',
    borderBottom: '1px solid #e7dfc9'
  },
  drawerTitle: {
    fontWeight: 800,
    lineHeight: 1.2,
    fontSize: 16,
    color: '#1c2419'
  },
  drawerSubtitle: {
    marginTop: 2,
    fontSize: 11.5,
    color: '#6f7a6b',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  drawerContent: {
    padding: theme.spacing(1.5),
    overflowY: 'auto',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25)
  },
  drawerTabs: {
    display: 'flex',
    padding: '0 14px',
    background: '#fbf8f1',
    borderBottom: '1px solid #e7dfc9',
    gap: 14,
    overflowX: 'auto',
    flexShrink: 0
  },
  drawerTabButton: {
    padding: '9px 0 10px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    flexShrink: 0,
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 700,
    color: '#97a094',
    borderBottom: '2px solid transparent',
    marginBottom: -1,
    letterSpacing: 0,
    whiteSpace: 'nowrap'
  },
  drawerTabButtonActive: {
    color: '#1c2419',
    borderBottomColor: '#1f5e3a'
  },
  drawerKpiRibbon: {
    padding: '12px 12px 11px',
    background: '#fbf8f1',
    borderBottom: '1px solid #e7dfc9',
    flexShrink: 0
  },
  drawerKpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 6
  },
  drawerChipRow: {
    display: 'flex',
    gap: 5,
    marginTop: 9,
    flexWrap: 'wrap'
  },
  v4Chip: {
    fontSize: 10.5,
    fontWeight: 700,
    padding: '3px 9px',
    borderRadius: 99,
    background: '#f4efe4',
    border: '1px solid #e7dfc9',
    color: '#3d463a',
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap'
  },
  v4GreenChip: {
    background: '#e3eee0',
    borderColor: '#c7dac8',
    color: '#164a2c'
  },
  v4MonoChip: {
    fontFamily: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
    letterSpacing: 0
  },
  v4AmberChip: {
    background: '#f7ecd4',
    borderColor: '#e3cf9b',
    color: '#8c5b00'
  },
  drawerScenarioNote: {
    marginTop: 9,
    padding: '7px 10px',
    borderRadius: 7,
    background: '#f7ecd4',
    border: '1px solid #e3cf9b',
    color: '#6e4a05',
    fontSize: 11,
    lineHeight: 1.45
  },
  drawerQuickStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))',
    gap: 6,
    marginTop: 10
  },
  drawerQuickStat: {
    minWidth: 0,
    padding: '7px 8px',
    borderRadius: 7,
    background: '#faf6ee',
    border: '1px solid #ede6d2'
  },
  drawerQuickStatLabel: {
    fontSize: 8.8,
    fontWeight: 800,
    color: '#97a094',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  drawerQuickStatValue: {
    marginTop: 3,
    fontSize: 13,
    fontWeight: 800,
    color: '#1c2419',
    fontFamily: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
    whiteSpace: 'nowrap'
  },
  drawerQuickStatSub: {
    marginTop: 2,
    fontSize: 9.5,
    fontWeight: 600,
    color: '#6f7a6b',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  v4Card: {
    background: '#fbf8f1',
    border: '1px solid #e7dfc9',
    borderRadius: 10,
    padding: '11px 13px 13px'
  },
  v4CardTight: {
    padding: '10px 12px 11px'
  },
  v4CardHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
    marginBottom: 8,
    flexWrap: 'wrap'
  },
  v4CardTitle: {
    fontSize: 11,
    fontWeight: 800,
    color: '#3d463a',
    letterSpacing: 0,
    whiteSpace: 'nowrap'
  },
  v4CardSubtitle: {
    fontSize: 10.5,
    color: '#97a094',
    fontWeight: 600,
    whiteSpace: 'nowrap'
  },
  v4ActionLink: {
    marginLeft: 'auto',
    fontSize: 10.5,
    fontWeight: 700,
    color: '#1f5e3a',
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    padding: 0,
    fontFamily: 'inherit'
  },
  drawerAlert: {
    padding: theme.spacing(1.25),
    borderRadius: 8,
    backgroundColor: 'var(--df-danger-soft, #f7dccf)',
    color: 'var(--df-danger, #a9341d)',
    fontSize: '0.85rem',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  miniRouteMap: {
    height: 170,
    border: '1px solid var(--df-line, #e0d8c2)',
    borderRadius: 8,
    background: 'linear-gradient(180deg, #eef2e9 0%, #f8f4e8 100%)',
    overflow: 'hidden',
    marginBottom: theme.spacing(1.25)
  },
  emptyDrawerNote: {
    padding: theme.spacing(1.25),
    borderRadius: 8,
    backgroundColor: 'var(--df-bg-2, #f1ece0)',
    color: 'var(--df-ink-3, #6f7a6b)',
    fontSize: '0.82rem',
    lineHeight: 1.45
  }
}))


export function MiniRouteMap ({ property, geoInsights, classes }) {
  const mapContainerRef = useRef(null)
  const mapRef = useRef(null)
  const tileLayerRef = useRef(null)
  const routeLayerRef = useRef(null)

  const siteCoords = geoInsights?.selectedCoords
  const yardRoute = geoInsights?.yardRoute
  const siteLat = siteCoords?.lat
  const siteLng = siteCoords?.lng
  const nearby = (geoInsights?.sitesWithinFiveMiles || []).slice(0, 8)
  const nearbyKey = nearby
    .map(site => `${getAspirePropertyId(site) || site._id || site.name}:${Number(site.distanceMiles || 0).toFixed(3)}`)
    .join('|')
  const routeKey = yardRoute?.geometry?.coordinates?.length
    ? `${yardRoute.geometry.coordinates.length}:${Number(yardRoute.distanceMiles || 0).toFixed(3)}:${Number(yardRoute.durationMinutes || 0).toFixed(2)}`
    : 'no-route'
  const propertyLabel = property?.name || 'Property'

  useEffect(() => {
    if (!mapContainerRef.current || !Number.isFinite(siteLat) || !Number.isFinite(siteLng)) return
    if (mapRef.current) {
      try {
        mapRef.current.stop()
        mapRef.current.off()
        mapRef.current.remove()
      } catch (_) {}
      mapRef.current = null
    }

    const map = L.map(mapContainerRef.current, {
      center: [siteLat, siteLng],
      zoom: 11,
      zoomControl: true,
      attributionControl: false,
      dragging: true,
      scrollWheelZoom: false,
      fadeAnimation: false,
      zoomAnimation: false,
      markerZoomAnimation: false
    })
    mapRef.current = map
    applyBasemapProvider({ map, tileLayerRef, provider: googleHybridProvider })

    const bounds = [[HMI_YARD.lat, HMI_YARD.lng], [siteLat, siteLng]]

    L.circleMarker([HMI_YARD.lat, HMI_YARD.lng], {
      radius: 8,
      color: '#fff',
      weight: 2,
      fillColor: '#1c2419',
      fillOpacity: 1
    }).bindTooltip('HMI Yard').addTo(map)

    L.circleMarker([siteLat, siteLng], {
      radius: 9,
      color: '#fff',
      weight: 3,
      fillColor: '#a9341d',
      fillOpacity: 1
    }).bindTooltip(propertyLabel).addTo(map)

    nearby.forEach(site => {
      const coords = getPropertyCoordinates(site)
      if (!coords) return
      bounds.push([coords.lat, coords.lng])
      L.circleMarker([coords.lat, coords.lng], {
        radius: 4,
        color: '#fff',
        weight: 1,
        fillColor: '#3454c1',
        fillOpacity: 0.85
      }).bindTooltip(`${site.name || 'Nearby site'} (${formatMiles(site.distanceMiles)})`).addTo(map)
    })

    const routeCoords = yardRoute?.geometry?.coordinates || []
    if (routeCoords.length >= 2) {
      const latlngs = routeCoords.map(([lng, lat]) => [lat, lng])
      routeLayerRef.current = L.polyline(latlngs, {
        color: '#1f5e3a',
        weight: 5,
        opacity: 0.9
      }).addTo(map)
      latlngs.forEach(latlng => bounds.push(latlng))
    }

    map.fitBounds(bounds, { padding: [22, 22], maxZoom: 15 })
    setTimeout(() => {
      if (mapRef.current === map) map.invalidateSize()
    }, 80)

    return () => {
      if (mapRef.current) {
        try {
          mapRef.current.stop()
          mapRef.current.off()
          mapRef.current.remove()
        } catch (_) {}
        mapRef.current = null
      }
      routeLayerRef.current = null
      tileLayerRef.current = null
    }
  }, [siteLat, siteLng, routeKey, nearbyKey, propertyLabel])

  if (!siteCoords) {
    return (
      <div className={classes.emptyDrawerNote}>
        No coordinates available for a map preview.
      </div>
    )
  }

  return (
    <>
      <div className={classes.miniRouteMap}>
        <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
      </div>
      {geoInsights.routeLoading && (
        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
          Loading road route...
        </Typography>
      )}
      {geoInsights.routeError && (
        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
          Road geometry unavailable: {geoInsights.routeError}
        </Typography>
      )}
    </>
  )
}

function DetailCard ({ classes, title, subtitle, action, onAction, right, children, tight = false }) {
  return (
    <div className={`${classes.v4Card} ${tight ? classes.v4CardTight : ''}`}>
      <div className={classes.v4CardHeader} style={{ marginBottom: tight ? 0 : 8 }}>
        <span className={classes.v4CardTitle}>{title}</span>
        {subtitle && <span className={classes.v4CardSubtitle}>{subtitle}</span>}
        {right && <span style={{ marginLeft: 'auto' }}>{right}</span>}
        {action && !right && (
          <button className={classes.v4ActionLink} onClick={onAction} type="button">
            {action} →
          </button>
        )}
      </div>
      {children}
    </div>
  )
}

function SegmentedControl ({ value, options, onChange }) {
  return (
    <div style={{ display: 'flex', background: V4_T.cream, borderRadius: 6, padding: 2, gap: 1, border: `1px solid ${V4_T.line}` }}>
      {options.map(option => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          type="button"
          style={{
            padding: '3px 9px',
            borderRadius: 4,
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 10,
            fontWeight: 700,
            background: value === option.value ? V4_T.surface : 'transparent',
            color: value === option.value ? V4_T.ink : V4_T.muteSoft,
            boxShadow: value === option.value ? '0 1px 2px rgba(28,36,25,0.08)' : 'none'
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

function Sparkline ({ values, width = 88, height = 22, color = V4_T.green, fill = true }) {
  const cleanValues = values.length ? values.map(value => safeNumber(value)) : [0, 0]
  const yMin = Math.min(...cleanValues)
  const yMax = Math.max(...cleanValues)
  const range = yMax - yMin || 1
  const xStep = cleanValues.length > 1 ? (width - 2) / (cleanValues.length - 1) : width - 2
  const points = cleanValues.map((value, index) => [
    1 + index * xStep,
    height - 2 - ((value - yMin) / range) * (height - 4)
  ])
  const path = points.map((point, index) => `${index ? 'L' : 'M'}${point[0].toFixed(1)} ${point[1].toFixed(1)}`).join(' ')
  const fillPath = `${path} L ${points[points.length - 1][0].toFixed(1)} ${height - 1} L ${points[0][0].toFixed(1)} ${height - 1} Z`

  return (
    <svg width={width} height={height} style={{ display: 'block', maxWidth: '100%' }}>
      {fill && <path d={fillPath} fill={color} opacity="0.12" />}
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={points[points.length - 1][0]} cy={points[points.length - 1][1]} r="2" fill={color} stroke={V4_T.surface} strokeWidth="1" />
    </svg>
  )
}

function RibbonKPI ({ label, value, unit, tone = 'ink', spark, color = V4_T.green }) {
  const valueColor = tone === 'bad' ? V4_T.rust : tone === 'good' ? V4_T.green : V4_T.ink
  return (
    <div style={{ padding: '9px 10px 10px', borderRadius: 8, background: V4_T.cream, border: `1px solid ${V4_T.line}`, minWidth: 0 }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: V4_T.muteSoft, textTransform: 'uppercase', letterSpacing: 0 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 2, marginTop: 3 }}>
        <span style={{ fontSize: 18, fontWeight: 800, color: valueColor, lineHeight: 1 }}>{value}</span>
        {unit && <span style={{ fontSize: 11, fontWeight: 700, color: V4_T.muteSoft }}>{unit}</span>}
      </div>
      {spark && <div style={{ marginTop: 5 }}><Sparkline values={spark} color={color} width={104} height={20} /></div>}
    </div>
  )
}

function OvertimeKPI ({ value, visits }) {
  const bars = visits.slice(-18)
  const maxHours = Math.max(1, ...bars.map(visit => visit.hrs))
  return (
    <div style={{ padding: '9px 10px 10px', borderRadius: 8, background: V4_T.cream, border: `1px solid ${V4_T.line}`, minWidth: 0 }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: V4_T.muteSoft, textTransform: 'uppercase', letterSpacing: 0 }}>Overtime</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 2, marginTop: 3 }}>
        <span style={{ fontSize: 18, fontWeight: 800, color: V4_T.ink, lineHeight: 1 }}>{formatNumber(value, 1)}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: V4_T.muteSoft }}>h</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 20, marginTop: 5 }}>
        {(bars.length ? bars : [{ hrs: 0 }]).map((visit, index) => (
          <div
            key={`${visit.id || 'bar'}-${index}`}
            style={{
              flex: 1,
              height: Math.max(3, (visit.hrs / maxHours) * 18),
              borderRadius: 1,
              background: visit.flag === 'high' ? V4_T.rust : V4_T.green,
              opacity: visit.flag === 'high' ? 0.95 : 0.5
            }}
          />
        ))}
      </div>
    </div>
  )
}

function HoursDotPlot ({ visits, est, onHover, onSelect, onOpen, width = 368, height = 122 }) {
  const rows = visits.length ? visits : [{ wAgo: 0, hrs: 0, type: 'Other' }]
  const padX = 8
  const padTop = 14
  const padBot = 22
  const weekMax = Math.max(1, ...rows.map(visit => visit.wAgo)) + 1
  const yMax = Math.max(1, est * 1.05, ...rows.map(visit => visit.hrs * 1.05))
  const xToPx = (weeksAgo) => padX + (1 - weeksAgo / weekMax) * (width - padX * 2)
  const yToPx = (hours) => padTop + (1 - hours / yMax) * (height - padTop - padBot)

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      <defs>
        <pattern id="routing-detail-dotgrid" width="14" height="14" patternUnits="userSpaceOnUse">
          <circle cx="0.6" cy="0.6" r="0.6" fill={V4_T.line} />
        </pattern>
      </defs>
      <rect x={padX} y={padTop} width={width - padX * 2} height={height - padTop - padBot} fill="url(#routing-detail-dotgrid)" opacity="0.5" />
      <line x1={padX} x2={width - padX} y1={yToPx(est)} y2={yToPx(est)} stroke={V4_T.rust} strokeWidth="1" strokeDasharray="3 3" opacity="0.55" />
      <text x={width - padX - 2} y={yToPx(est) - 4} textAnchor="end" fontSize="9.5" fontWeight="700" fill={V4_T.rust} fontFamily={V4_T.mono}>EST {formatNumber(est, 1)}h</text>
      <line x1={padX} x2={width - padX} y1={yToPx(0)} y2={yToPx(0)} stroke={V4_T.line} strokeWidth="1" />
      {rows.map((visit, index) => {
        const x = xToPx(visit.wAgo)
        const y = yToPx(visit.hrs)
        const color = SERVICE_COLORS[visit.type] || V4_T.mute
        const previewVisit = () => onHover && onHover(visit)
        const selectVisit = () => onSelect ? onSelect(visit) : previewVisit()
        const openVisit = () => onOpen && onOpen(visit)
        return (
          <g
            key={`${visit.id || 'visit'}-${index}`}
            role="button"
            tabIndex="0"
            onMouseEnter={previewVisit}
            onClick={selectVisit}
            onDoubleClick={openVisit}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                selectVisit()
              } else if (event.key === 'o' || event.key === 'O') {
                openVisit()
              }
            }}
            style={{ cursor: 'pointer', outline: 'none' }}
          >
            <line x1={x} x2={x} y1={yToPx(0)} y2={y} stroke={color} strokeWidth="1" opacity="0.18" />
            <circle cx={x} cy={y} r={visit.flag === 'high' ? 4.5 : 3.5} fill={color} stroke={V4_T.surface} strokeWidth="1.4" opacity="0.92" />
            {visit.flag === 'high' && <circle cx={x} cy={y} r="7" fill="none" stroke={color} strokeWidth="1" opacity="0.35" />}
          </g>
        )
      })}
      {[weekMax - 1, Math.floor(weekMax / 2), 0].map((weeksAgo, index) => (
        // Key by position, not value: when weekMax is small (sparse history) the
        // three computed weeksAgo values collide (e.g. [1,1,0]) → duplicate-key warning.
        <text key={index} x={xToPx(weeksAgo)} y={height - 6} textAnchor={index === 0 ? 'start' : index === 2 ? 'end' : 'middle'} fontSize="9.5" fill={V4_T.muteSoft} fontWeight="600" fontFamily={V4_T.mono}>
          {weeksAgo === 0 ? 'NOW' : weeksAgo === Math.floor(weekMax / 2) ? `${weeksAgo}W` : `${weeksAgo}W AGO`}
        </text>
      ))}
    </svg>
  )
}

function MonthlyHoursBars ({ monthly, est, width = 368, height = 104 }) {
  const padX = 4
  const padTop = 12
  const padBot = 18
  const yMax = Math.max(1, est * 1.1, ...monthly.map(month => month.act)) * 1.05
  const innerW = width - padX * 2
  const innerH = height - padTop - padBot
  const colW = innerW / Math.max(monthly.length, 1)
  const yToPx = (hours) => padTop + (1 - hours / yMax) * innerH

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      <line x1={padX} x2={width - padX} y1={yToPx(0)} y2={yToPx(0)} stroke={V4_T.line} strokeWidth="1" />
      <line x1={padX} x2={width - padX} y1={yToPx(est)} y2={yToPx(est)} stroke={V4_T.rust} strokeWidth="1" strokeDasharray="2 3" opacity="0.5" />
      {monthly.map((month, index) => {
        const x = padX + index * colW + colW * 0.18
        const barW = colW * 0.64
        const yActual = yToPx(month.act)
        const over = month.act > est
        return (
          <g key={month.key || index}>
            <rect x={x} y={yActual} width={barW} height={yToPx(0) - yActual} rx="2" fill={over ? V4_T.rust : V4_T.green} opacity={month.current ? 1 : 0.85} />
            <text x={x + barW / 2} y={height - 5} textAnchor="middle" fontSize="9.5" fontWeight={month.current ? 800 : 600} fill={month.current ? V4_T.ink : V4_T.muteSoft} fontFamily={V4_T.mono}>{month.short}</text>
          </g>
        )
      })}
    </svg>
  )
}

function RevCostBars ({ monthly, width = 368, height = 96 }) {
  const padX = 4
  const padTop = 8
  const padBot = 16
  const yMax = Math.max(1, ...monthly.map(month => Math.max(month.rev + month.othRev, month.cost))) * 1.1
  const innerW = width - padX * 2
  const innerH = height - padTop - padBot
  const colW = innerW / Math.max(monthly.length, 1)
  const yToPx = (value) => padTop + (1 - value / yMax) * innerH

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      <line x1={padX} x2={width - padX} y1={yToPx(0)} y2={yToPx(0)} stroke={V4_T.line} strokeWidth="1" />
      {monthly.map((month, index) => {
        const center = padX + index * colW + colW / 2
        const totalRev = month.rev + month.othRev
        return (
          <g key={month.key || index}>
            <rect x={center - 6} y={yToPx(month.rev)} width="5" height={yToPx(0) - yToPx(month.rev)} fill={V4_T.green} opacity="0.88" rx="1" />
            {month.othRev > 0 && <rect x={center - 6} y={yToPx(totalRev)} width="5" height={yToPx(month.rev) - yToPx(totalRev)} fill={V4_T.amber} opacity="0.92" rx="1" />}
            <rect x={center + 1} y={yToPx(month.cost)} width="5" height={yToPx(0) - yToPx(month.cost)} fill={V4_T.mute} opacity="0.75" rx="1" />
            <text x={center} y={height - 4} textAnchor="middle" fontSize="9" fontWeight={month.current ? 700 : 500} fill={month.current ? V4_T.ink : V4_T.muteSoft} fontFamily={V4_T.mono}>{month.short}</text>
          </g>
        )
      })}
    </svg>
  )
}

function ServiceBars ({ services }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {services.map(service => {
        const color = SERVICE_COLORS[service.name] || V4_T.mute
        return (
          <div key={service.name}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 3 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
              <span style={{ fontSize: 11.5, fontWeight: 600, color: V4_T.ink2, flex: 1 }}>{service.name}</span>
              <span style={{ fontSize: 10.5, color: V4_T.muteSoft, fontFamily: V4_T.mono, fontWeight: 600 }}>{service.completed}/{service.tickets} tx</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: V4_T.ink, fontFamily: V4_T.mono, minWidth: 42, textAlign: 'right' }}>{formatNumber(service.hours, 1)}h</span>
            </div>
            <div style={{ height: 4, background: V4_T.line, borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${clamp(service.share * 100, 0, 100)}%`, background: color, borderRadius: 2 }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function DrawerQuickStat ({ classes, label, value, sub, tone, termKey, property }) {
  const valueColor = tone === 'bad' ? V4_T.rust : tone === 'good' ? V4_T.green : tone === 'warn' ? '#a87320' : V4_T.ink
  return (
    <div className={classes.drawerQuickStat}>
      <div className={classes.drawerQuickStatLabel}>{label}</div>
      <div className={classes.drawerQuickStatValue} style={{ color: valueColor }}>
        {termKey ? <HoursValue termKey={termKey} property={property}>{value}</HoursValue> : value}
      </div>
      {sub && <div className={classes.drawerQuickStatSub}>{sub}</div>}
    </div>
  )
}

function PropertyDetailDrawer (props) {
  // Dual-mode: controlled callers (RoutingPropertiesTable, RefineRouteTab) pass
  // sidebarData plus ~25 flat props; self-fetch callers pass only
  // { property, url, allRows, demoMode }. Hooks below are ALWAYS called
  // (rules of hooks); in controlled mode their values are ignored.
  const isControlled = props.sidebarData != null
  const detailProperty = props.property || props.selectedProperty
  const bundle = usePropertyDetailController({
    property: detailProperty,
    url: props.url,
    allRows: props.allRows,
    demoMode: props.demoMode,
    enabled: !isControlled,
    billingGroupEnabled: !!props.url && !!detailProperty
  })
  const routeDrive = useRouteInterStopDrive({
    property: detailProperty,
    routes: props.routes,                       // undefined when consumer has none → hook self-fetches
    selectedScenarioId: props.selectedScenarioId || null,
    url: props.url
  })
  const profitSettings = useProfitabilitySettings(props.url)
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const ownClasses = useRoutingPropertyDetailStyles()
  const classes = props.classes || ownClasses
  const selectedProperty = isControlled ? props.selectedProperty : props.property
  const sidebarData = isControlled ? props.sidebarData : bundle.sidebarData
  const profitabilitySidebarDataBase = isControlled ? (props.profitabilitySidebarData || sidebarData) : (bundle.profitabilitySidebarData || sidebarData)
  const profitabilitySidebarData = routeDrive?.source
    ? { ...profitabilitySidebarDataBase, driveOverride: { hours: routeDrive.driveHours, miles: routeDrive.driveMiles, source: routeDrive.source } }
    : profitabilitySidebarDataBase
  const geoInsights = isControlled ? props.geoInsights : bundle.geoInsights
  const activeTab = isControlled ? props.activeTab : bundle.activeTab
  const onTabChange = isControlled ? props.onTabChange : bundle.setActiveTab
  const hoursView = isControlled ? props.hoursView : bundle.hoursView
  const setHoursView = isControlled ? props.setHoursView : bundle.setHoursView
  const detailHistoryRange = isControlled ? props.detailHistoryRange : bundle.detailHistoryRange
  const setDetailHistoryRange = isControlled ? props.setDetailHistoryRange : bundle.setDetailHistoryRange
  const detailHistory = isControlled ? props.detailHistory : bundle.detailHistory
  const hoveredVisit = isControlled ? props.hoveredVisit : bundle.hoveredVisit
  const setHoveredVisit = isControlled ? props.setHoveredVisit : bundle.setHoveredVisit
  const pinnedVisit = isControlled ? props.pinnedVisit : bundle.pinnedVisit
  const setPinnedVisit = isControlled ? props.setPinnedVisit : bundle.setPinnedVisit
  const visitTypeFilter = isControlled ? props.visitTypeFilter : bundle.visitTypeFilter
  const setVisitTypeFilter = isControlled ? props.setVisitTypeFilter : bundle.setVisitTypeFilter
  const trendPct = isControlled ? props.trendPct : bundle.trendPct
  const marginSpark = isControlled ? props.marginSpark : bundle.marginSpark
  const visitsSpark = isControlled ? props.visitsSpark : bundle.visitsSpark
  const dayAbbrev = isControlled ? props.dayAbbrev : bundle.dayAbbrev
  const dominantService = isControlled ? props.dominantService : bundle.dominantService
  const isProblematic = isControlled ? props.isProblematic : bundle.isProblematic
  // Billing group for the header chip. Prefer the same source the Profitability
  // panel already uses successfully — profitabilitySidebarData.billingGroup,
  // which is populated in BOTH controlled and uncontrolled modes. Fall back to
  // the controller fetch, a controlled prop, or the group on the row.
  const billingGroupState = isControlled ? props.billingGroup : bundle.billingGroup
  const billingGroupData = (profitabilitySidebarData && profitabilitySidebarData.billingGroup)
    || (billingGroupState && billingGroupState.data)
    || (selectedProperty && selectedProperty.billingGroup)
    || null
  const billingGroupMemberCount = billingGroupData
    ? (Array.isArray(billingGroupData.members) && billingGroupData.members.length) ||
      (Array.isArray(billingGroupData.memberAspirePropertyIds) ? billingGroupData.memberAspirePropertyIds.length : 0)
    : 0
  const billingGroupName = billingGroupData && billingGroupData.name
  const { open, onClose, onEditProperty } = props
  const kpis = sidebarData.kpis
  // Margin is billing-scoped: show the group margin when grouped (same source as
  // the Financials/Profitability tabs). OT + Visits stay per-property. Pass the
  // SAME config the Profitability panel's headline uses (global settings +
  // default 'actual' hours basis + driveOverride) so the ribbon % equals the
  // panel's "current margin".
  const ribbonMargin = getRibbonMargin(sidebarData, profitabilitySidebarData, {
    ...(profitSettings.settings || {}),
    hoursBasis: 'actual',
    driveOverride: profitabilitySidebarData?.driveOverride || null
  })
  const driveVariance = kpis.driveActual - kpis.driveExpected
  const hasNormalizedMedian = kpis.normalizedVisitCount > 0
  const splitVisitGroups = sidebarData.splitUpcomingVisitGroups || []
  const splitVisitCount = splitVisitGroups[0]?.splitCount || 0

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <div className={classes.drawer}>
        <div className={classes.drawerHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5, color: V4_T.muteSoft, fontFamily: V4_T.mono, marginBottom: 6 }}>
            <span>PROPERTIES</span>
            <span>›</span>
            <span style={{ color: V4_T.ink2 }}>#{sidebarData.propertyId || '-'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" className={classes.drawerTitle}>
                {selectedProperty?.name || 'Property Details'}
              </Typography>
              <Typography variant="body2" className={classes.drawerSubtitle}>
                {formatAddress(sidebarData.address) || 'Address pending'}
              </Typography>
            </div>
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              <HoursGlossaryButton property={selectedProperty} />
              {onEditProperty && (
                <Tooltip title="Edit property">
                  <IconButton size="small" onClick={onEditProperty} style={{ border: `1px solid ${V4_T.line}`, borderRadius: 6, padding: 5 }}>
                    <EditIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={selectedProperty?.jobsiteId ? 'Open in Takeoffs' : 'No takeoff site linked'}>
                <span>
                  <IconButton
                    size="small"
                    component="a"
                    href={selectedProperty?.jobsiteId ? `/site-takeoff/${selectedProperty.jobsiteId}` : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={!selectedProperty?.jobsiteId}
                    style={{ border: `1px solid ${V4_T.line}`, borderRadius: 6, padding: 5 }}
                  >
                    <MapIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Open in Aspire">
                <span>
                  <IconButton
                    size="small"
                    component="a"
                    href={sidebarData.aspireUrl || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={!sidebarData.aspireUrl}
                    style={{ border: `1px solid ${V4_T.line}`, borderRadius: 6, padding: 5 }}
                  >
                    <OpenInNewIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="More actions">
                <IconButton size="small" onClick={(e) => setMoreMenuAnchor(e.currentTarget)} style={{ border: `1px solid ${V4_T.line}`, borderRadius: 6, padding: 5 }}>
                  <MoreVertIcon style={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Menu anchorEl={moreMenuAnchor} open={!!moreMenuAnchor} onClose={() => setMoreMenuAnchor(null)}>
                <MenuItem onClick={() => { setMoreMenuAnchor(null); setSettingsOpen(true) }}>Profitability settings…</MenuItem>
              </Menu>
              <Tooltip title="Close">
                <IconButton size="small" onClick={onClose} style={{ border: `1px solid ${V4_T.line}`, borderRadius: 6, padding: 5 }}>
                  <CloseIcon style={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          <div className={classes.drawerChipRow}>
            {sidebarData.propertyId && <span className={`${classes.v4Chip} ${classes.v4MonoChip}`}>ASPIRE {sidebarData.propertyId}</span>}
            {sidebarData.companyChip && <span className={`${classes.v4Chip} ${classes.v4MonoChip}`}>{sidebarData.companyChip}</span>}
            {billingGroupName && (
              <Tooltip title={`Billing group "${billingGroupName}" — ${billingGroupMemberCount} ${billingGroupMemberCount === 1 ? 'property' : 'properties'} billed together`}>
                <span className={`${classes.v4Chip} ${classes.v4GreenChip}`}>
                  🧾 {billingGroupName}{billingGroupMemberCount ? ` · ${billingGroupMemberCount}` : ''}
                </span>
              </Tooltip>
            )}
            {sidebarData.crewName && sidebarData.crewName !== '-' && (
              <span className={`${classes.v4Chip} ${classes.v4GreenChip}`}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: V4_T.green, marginRight: 5, flexShrink: 0 }} />
                {sidebarData.crewName}
              </span>
            )}
            {sidebarData.scheduledDay && <span className={classes.v4Chip}>{sidebarData.scheduledDay}</span>}
            {splitVisitGroups.length > 0 && (
              <Tooltip title={`${splitVisitGroups.length} upcoming work ${splitVisitGroups.length === 1 ? 'ticket requires' : 'tickets require'} multiple crew visits`}>
                <span className={`${classes.v4Chip} ${classes.v4AmberChip}`}>
                  Split ticket · {splitVisitCount} crew visits
                </span>
              </Tooltip>
            )}
            {sidebarData.parkedCrewName && (
              <span className={`${classes.v4Chip} ${classes.v4AmberChip}`}>⚠ Parked · {sidebarData.parkedCrewName}</span>
            )}
            {sidebarData.scenarioUnscheduled && (
              <span className={`${classes.v4Chip} ${classes.v4AmberChip}`}>⚠ No day in scenario</span>
            )}
          </div>
          {(sidebarData.parkedCrewName || sidebarData.scenarioUnscheduled) && (
            <div className={classes.drawerScenarioNote}>
              {sidebarData.parkedCrewName
                ? `Parked in this scenario on "${sidebarData.parkedCrewName}"${sidebarData.parkedCrewInactive ? ' (inactive crew)' : ''}${sidebarData.parkedCrewDivision ? ` · ${sidebarData.parkedCrewDivision}` : ''} — not on any active maintenance board. Data below is the live Aspire schedule.`
                : 'In this scenario the property is on the route but not placed on a day. Data below is the live Aspire schedule.'}
            </div>
          )}
          <div className={classes.drawerQuickStats}>
            {kpis.hasPrescribedHours && (
              <DrawerQuickStat
                classes={classes}
                label="Prescribed"
                value={`${formatNumber(kpis.prescribedClockHours, 1)}h`}
                sub={`${formatNumber(kpis.prescribedManHours, 1)} man-hrs · ${kpis.prescribedCrewSize} crew`}
              />
            )}
            <DrawerQuickStat
              classes={classes}
              label="Est hours"
              value={`${formatNumber(kpis.hasInternalEst ? kpis.internalEstHours : kpis.estHoursPerVisit, 1)}h`}
              sub={kpis.hasInternalEst
                ? `internal · ${kpis.internalEstVisitCount} visit${kpis.internalEstVisitCount === 1 ? '' : 's'}`
                : 'contract setup'}
              termKey="internalEstimate"
              property={selectedProperty}
            />
            <DrawerQuickStat
              classes={classes}
              label="Median on-site"
              value={`${formatNumber(kpis.medianHours || kpis.avgHours, 2)}h`}
              sub={hasNormalizedMedian ? `${kpis.normalizedVisitCount} clock rows` : 'crew-hour fallback'}
              tone={!hasNormalizedMedian ? 'warn' : kpis.medianHours > kpis.estHoursPerVisit ? 'bad' : 'good'}
              termKey="medianOnSite"
              property={selectedProperty}
            />
            <DrawerQuickStat
              classes={classes}
              label="Drive time"
              value={`${formatNumber(kpis.driveActual, 2)}h`}
              sub={`vs ${formatNumber(kpis.driveExpected, 2)}h route`}
              tone={driveVariance > 0 ? 'bad' : 'good'}
              termKey="driveTime"
              property={selectedProperty}
            />
          </div>
        </div>

        <div className={classes.drawerTabs}>
          {PROPERTY_DETAIL_TABS.map(tab => (
            <button
              key={tab.value}
              type="button"
              onClick={() => onTabChange(tab.value)}
              className={`${classes.drawerTabButton} ${activeTab === tab.value ? classes.drawerTabButtonActive : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className={classes.drawerKpiRibbon}>
          <div className={classes.drawerKpiGrid}>
            <RibbonKPI
              label="Margin"
              value={formatPercent(ribbonMargin.margin)}
              tone={ribbonMargin.margin < 10 ? 'bad' : 'good'}
              spark={ribbonMargin.spark.length ? ribbonMargin.spark : marginSpark}
              color={ribbonMargin.margin < 10 ? V4_T.rust : V4_T.green}
            />
            <OvertimeKPI value={kpis.otHours} visits={sidebarData.visitRows} />
            <RibbonKPI label="Visits" value={kpis.completedVisits} unit={`/${kpis.annualVisits || 0}`} spark={visitsSpark} color={V4_T.orange} />
          </div>
        </div>

        <div className={classes.drawerContent}>
          {isProblematic && (
            <div className={classes.drawerAlert}>
              <WarningIcon fontSize="small" />
              Review pricing or route assumptions for this property.
            </div>
          )}

          {activeTab === 'overview' && (
            <>
              <DetailCard
                classes={classes}
                title="Location"
                subtitle={geoInsights.hasCoordinates
                  ? `${geoInsights.yardRoute ? formatMiles(geoInsights.yardRoute.distanceMiles) : formatMiles(geoInsights.yardRoadMiles)} from yard`
                  : 'coordinates pending'}
              >
                {geoInsights.hasCoordinates ? (
                  <>
                    <MiniRouteMap property={selectedProperty} geoInsights={geoInsights} classes={classes} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 2px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: V4_T.ink, border: `2px solid ${V4_T.surface}`, boxShadow: `0 0 0 1px ${V4_T.line}` }} />
                        <span style={{ fontSize: 8.5, fontWeight: 800, color: V4_T.muteSoft, marginTop: 3 }}>YARD</span>
                      </div>
                      <div style={{ flex: 1, position: 'relative', height: 22 }}>
                        <div style={{ position: 'absolute', top: 10, left: 0, right: 0, height: 2, background: V4_T.green, borderRadius: 1 }} />
                        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <span style={{ fontSize: 12, fontWeight: 800, color: V4_T.ink, fontFamily: V4_T.mono, background: V4_T.surface, padding: '0 8px', lineHeight: 1.1 }}>
                            {geoInsights.yardRoute
                              ? `${formatMiles(geoInsights.yardRoute.distanceMiles)} · ${formatMinutes(geoInsights.yardRoute.durationMinutes)}`
                              : `${formatMiles(geoInsights.yardRoadMiles)} · ${formatMinutes(geoInsights.yardDriveMinutes)}`}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: V4_T.green, border: `2px solid ${V4_T.surface}`, boxShadow: `0 0 0 1px ${V4_T.green}` }} />
                        <span style={{ fontSize: 8.5, fontWeight: 800, color: V4_T.green, marginTop: 3 }}>SITE</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 10.5, color: V4_T.muteSoft, marginTop: 4, padding: '0 4px' }}>
                      <span>{formatMiles(geoInsights.yardStraightMiles)} straight-line</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 10px', marginTop: 10, borderRadius: 7, background: V4_T.surface2, border: `1px solid ${V4_T.lineSoft}` }}>
                      <span style={{ width: 22, height: 22, borderRadius: '50%', background: V4_T.greenSoft, color: V4_T.green, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>●</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 9.5, fontWeight: 800, color: V4_T.muteSoft, textTransform: 'uppercase' }}>Nearest customer</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: V4_T.ink, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {geoInsights.nearestSite?.name || 'None found'}
                        </div>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: V4_T.green, fontFamily: V4_T.mono, flexShrink: 0 }}>
                        {geoInsights.nearestSite ? formatMiles(geoInsights.nearestSite.distanceMiles) : '-'}
                      </span>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 6 }}>
                        <span style={{ fontSize: 9.5, fontWeight: 800, color: V4_T.muteSoft, textTransform: 'uppercase' }}>Neighborhood density</span>
                        <span style={{ marginLeft: 'auto', fontSize: 10.5, color: V4_T.mute, fontFamily: V4_T.mono }}>
                          avg <b style={{ color: V4_T.ink2 }}>{formatMiles(geoInsights.avgDistanceWithinFiveMiles)}</b> · med <b style={{ color: V4_T.ink2 }}>{formatMiles(geoInsights.medianDistanceWithinFiveMiles)}</b>
                        </span>
                      </div>
                      {[
                        ['1 MI', geoInsights.sitesWithinOneMile.length],
                        ['3 MI', geoInsights.sitesWithinThreeMiles.length],
                        ['5 MI', geoInsights.sitesWithinFiveMiles.length]
                      ].map(([label, count]) => {
                        const maxCount = Math.max(1, geoInsights.sitesWithinFiveMiles.length)
                        return (
                          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 5 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: V4_T.muteSoft, fontFamily: V4_T.mono, width: 32, flexShrink: 0 }}>{label}</span>
                            <div style={{ flex: 1, height: 14, background: V4_T.cream, borderRadius: 3, position: 'relative', overflow: 'hidden' }}>
                              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${clamp((count / maxCount) * 100, 0, 100)}%`, background: V4_T.green, opacity: 0.75, borderRadius: 3 }} />
                              <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 10, fontWeight: 700, color: V4_T.ink2, fontFamily: V4_T.mono }}>sites</span>
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 800, color: V4_T.ink, fontFamily: V4_T.mono, minWidth: 30, textAlign: 'right' }}>{count}</span>
                          </div>
                        )
                      })}
                    </div>
                  </>
                ) : (
                  <div className={classes.emptyDrawerNote}>No coordinates available for nearby-site or yard travel estimates.</div>
                )}
              </DetailCard>

              <DetailCard classes={classes} title="Cadence" action="Schedule" onAction={() => onTabChange('schedule')} tight>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, background: V4_T.greenSoft, border: '1px solid #c7dac8', marginTop: 2 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3px 9px', borderRadius: 6, background: V4_T.green, color: V4_T.surface, flexShrink: 0 }}>
                    <span style={{ fontSize: 9, fontWeight: 700 }}>EVERY</span>
                    <span style={{ fontSize: 13, fontWeight: 800 }}>{dayAbbrev}</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: V4_T.ink }}>{sidebarData.crewName || 'Crew pending'}</div>
                    <div style={{ fontSize: 11, color: V4_T.mute, marginTop: 1 }}>
                      Maintenance · {kpis.completedVisits} done · <span style={{ color: V4_T.ink2, fontWeight: 600 }}>{kpis.futureVisits} ahead</span>
                    </div>
                  </div>
                </div>
              </DetailCard>

              <HoursPerVisitCard
                classes={classes}
                sidebarData={sidebarData}
                hoursView={hoursView}
                setHoursView={setHoursView}
                detailHistoryRange={detailHistoryRange}
                setDetailHistoryRange={setDetailHistoryRange}
                hoveredVisit={hoveredVisit}
                setHoveredVisit={setHoveredVisit}
                pinnedVisit={pinnedVisit}
                setPinnedVisit={setPinnedVisit}
                visitTypeFilter={visitTypeFilter}
                setVisitTypeFilter={setVisitTypeFilter}
                trendPct={trendPct}
              />

              <CrewTimeCard classes={classes} sidebarData={sidebarData} />
            </>
          )}

          {activeTab === 'history' && (
            <HistoryTab
              classes={classes}
              sidebarData={sidebarData}
              detailHistoryRange={detailHistoryRange}
              setDetailHistoryRange={setDetailHistoryRange}
              detailHistory={detailHistory}
              hoveredVisit={hoveredVisit}
              setHoveredVisit={setHoveredVisit}
              pinnedVisit={pinnedVisit}
              setPinnedVisit={setPinnedVisit}
              visitTypeFilter={visitTypeFilter}
              setVisitTypeFilter={setVisitTypeFilter}
            />
          )}

          {activeTab === 'service' && (
            <ServiceTab classes={classes} sidebarData={sidebarData} dominantService={dominantService} />
          )}

          {activeTab === 'schedule' && (
            <ScheduleTab classes={classes} selectedProperty={selectedProperty} sidebarData={sidebarData} dayAbbrev={dayAbbrev} renderTicketLink={props.renderTicketLink} />
          )}

          {activeTab === 'financials' && (
            <FinancialsTab classes={classes} sidebarData={profitabilitySidebarData} />
          )}

          {activeTab === 'profitability' && (
            <ProfitabilityPanel classes={classes} sidebarData={profitabilitySidebarData} profitSettings={profitSettings.settings} />
          )}
        </div>
      </div>
      <ProfitabilitySettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        url={props.url}
        settings={profitSettings.settings}
        onSaved={profitSettings.refresh}
      />
    </Drawer>
  )
}

function HoursPerVisitCard ({
  classes,
  sidebarData,
  hoursView,
  setHoursView,
  detailHistoryRange,
  setDetailHistoryRange,
  hoveredVisit,
  setHoveredVisit,
  pinnedVisit,
  setPinnedVisit,
  visitTypeFilter,
  setVisitTypeFilter,
  trendPct
}) {
  const kpis = sidebarData.kpis
  const visibleVisitRows = filterVisitRowsByType(sidebarData.metricVisitRows || sidebarData.visitRows, visitTypeFilter)
  const visibleTrend = getVisitTrendStats(visibleVisitRows, kpis.avgHours)
  const activeVisit = pinnedVisit || hoveredVisit
  const previewVisit = (visit) => {
    if (!pinnedVisit) setHoveredVisit(visit)
  }
  const openVisit = (visit) => {
    const visitUrl = getAspireWorkTicketVisitUrl(visit) || getAspireWorkTicketUrl(visit)
    if (visitUrl) window.open(visitUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <DetailCard
      classes={classes}
      title="Hours per visit"
      subtitle="trailing year"
      right={
        <SegmentedControl
          value={hoursView}
          options={[{ value: 'visits', label: 'Visits' }, { value: 'months', label: 'Months' }]}
          onChange={setHoursView}
        />
      }
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 26, fontWeight: 800, color: kpis.estVariance >= 0 ? V4_T.green : V4_T.rust, lineHeight: 1 }}>{formatNumber(kpis.avgHours, 1)}h</span>
        <span style={{ fontSize: 11.5, color: V4_T.mute, whiteSpace: 'nowrap' }}>
          avg · vs <span style={{ fontFamily: V4_T.mono, color: V4_T.ink2, fontWeight: 600 }}>{formatNumber(kpis.estHoursPerVisit, 1)}h</span> est
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: kpis.estVariance >= 0 ? '#164a2c' : V4_T.rust, padding: '3px 9px', borderRadius: 99, background: kpis.estVariance >= 0 ? V4_T.greenSoft : V4_T.rustSoft, whiteSpace: 'nowrap' }}>
          {Math.abs(kpis.estVariance).toFixed(1)}h {kpis.estVariance >= 0 ? 'under' : 'over'}
        </span>
      </div>

      {hoursView === 'months' ? (
        <>
          <div style={{ background: V4_T.cream, borderRadius: 8, padding: '6px 4px 2px', margin: '0 -3px' }}>
            <MonthlyHoursBars monthly={sidebarData.monthlyRows} est={kpis.estHoursPerVisit} />
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 7, fontSize: 10.5, color: V4_T.mute, alignItems: 'center' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: 2, background: V4_T.green }} /> Actual</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 14, borderTop: `1.5px dashed ${V4_T.rust}` }} /> Estimate</span>
            <span style={{ marginLeft: 'auto', color: trendPct > 0 ? V4_T.rust : V4_T.green, fontWeight: 700 }}>{trendPct > 0 ? 'Trending up' : 'Trending down'}</span>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 11.5, color: V4_T.mute }}>
              <b style={{ color: V4_T.ink, fontFamily: V4_T.mono }}>{formatNumber(visibleTrend.recentAvg, 2)}h</b> recent avg
              <span style={{ color: visibleTrend.trendPct > 0 ? V4_T.rust : V4_T.green, fontWeight: 700, marginLeft: 6 }}>{visibleTrend.trendPct > 0 ? '+' : ''}{visibleTrend.trendPct}%</span>
              <span style={{ color: V4_T.muteSoft }}> vs prior 8wk</span>
            </span>
            <div style={{ marginLeft: 'auto' }}>
              <SegmentedControl
                value={detailHistoryRange}
                options={[{ value: '30', label: '30d' }, { value: '90', label: '90d' }, { value: 'year', label: '1y' }]}
                onChange={setDetailHistoryRange}
              />
            </div>
          </div>
          <div style={{ background: V4_T.cream, borderRadius: 8, padding: '6px 2px 2px', margin: '0 -3px' }}>
            <HoursDotPlot
              visits={visibleVisitRows}
              est={kpis.estHoursPerVisit}
              onHover={previewVisit}
              onSelect={setPinnedVisit}
              onOpen={openVisit}
            />
          </div>
          <VisitLegend value={visitTypeFilter} onChange={(nextFilter) => {
            setVisitTypeFilter(nextFilter)
            setHoveredVisit(null)
            setPinnedVisit(null)
          }} />
          {activeVisit && <HoverStrip visit={activeVisit} pinned={!!pinnedVisit} onClear={() => {
            setPinnedVisit(null)
            setHoveredVisit(null)
          }} />}
        </>
      )}

      <div style={{ marginTop: 10, padding: '10px 12px', background: kpis.estVariance >= 0 ? V4_T.greenSoft : V4_T.rustSoft, border: `1px solid ${kpis.estVariance >= 0 ? '#c7dac8' : '#e8c4a8'}`, borderRadius: 8, fontSize: 11.5, color: kpis.estVariance >= 0 ? '#164a2c' : '#7a3115', lineHeight: 1.45 }}>
        Crew runs <strong>{Math.abs(kpis.estVariance).toFixed(1)}h {kpis.estVariance >= 0 ? 'under' : 'over'} estimate per visit</strong> based on current routing rollups.
      </div>
    </DetailCard>
  )
}

function HoverStrip ({ visit, pinned = false, onClear }) {
  const visitUrl = getAspireWorkTicketVisitUrl(visit)
  const ticketUrl = getAspireWorkTicketUrl(visit)
  const linkUrl = visitUrl || ticketUrl
  const linkLabel = visitUrl ? 'Open visit' : getTicketLabel(visit)

  return (
    <div style={{ marginTop: 8, padding: '6px 9px', background: V4_T.greenSofter, border: '1px solid #cfe0d0', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 11.5 }}>
      <span style={{ fontFamily: V4_T.mono, color: V4_T.ink2 }}>{visit.date}</span>
      <span style={{ color: V4_T.mute, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{visit.crew}</span>
      <span style={{ fontWeight: 700, color: V4_T.ink, fontFamily: V4_T.mono }}>{formatNumber(visit.hrs, 2)}h</span>
      {linkUrl && (
        <MuiLink
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
          style={{ color: V4_T.green, fontWeight: 800, fontFamily: V4_T.mono, whiteSpace: 'nowrap' }}
        >
          {linkLabel}
        </MuiLink>
      )}
      {pinned && (
        <button
          type="button"
          onClick={onClear}
          style={{ border: 'none', background: 'transparent', color: V4_T.muteSoft, cursor: 'pointer', fontSize: 13, fontWeight: 800, lineHeight: 1, padding: 2 }}
          aria-label="Clear selected visit"
          title="Clear selected visit"
        >
          x
        </button>
      )}
    </div>
  )
}

function VisitLegend ({ value = 'all', onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 7, flexWrap: 'wrap', fontSize: 10.5 }}>
      <button
        type="button"
        onClick={() => onChange && onChange('all')}
        style={{
          border: `1px solid ${value === 'all' ? V4_T.green : V4_T.line}`,
          background: value === 'all' ? V4_T.greenSofter : 'transparent',
          borderRadius: 999,
          padding: '2px 8px',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 10.5,
          fontWeight: 800,
          color: value === 'all' ? V4_T.ink : V4_T.mute
        }}
        aria-pressed={value === 'all'}
        title="Show all visit dots"
      >
        All
      </button>
      {[['Maint', 'Maintenance'], ['Irrig', 'Irrigation'], ['Herb', 'Herbicide']].map(([key, label]) => {
        const active = value === key
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange && onChange(active ? 'all' : key)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              border: `1px solid ${active ? V4_T.green : 'transparent'}`,
              background: active ? V4_T.greenSofter : 'transparent',
              borderRadius: 999,
              padding: '2px 7px',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 10.5
            }}
            aria-pressed={active}
            title={`Show ${label} dots`}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: SERVICE_COLORS[key] }} />
            <span style={{ fontWeight: 700, color: active ? V4_T.ink : V4_T.mute }}>{label}</span>
          </button>
        )
      })}
      <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 14, borderTop: `1.5px dashed ${V4_T.rust}` }} />
        <span style={{ fontWeight: 600, color: V4_T.rust }}>contract target</span>
      </span>
    </div>
  )
}

function CrewTimeCard ({ classes, sidebarData }) {
  const total = sidebarData.kpis.hoursActual
  const onSite = sidebarData.kpis.avgHours
  const driveActual = sidebarData.kpis.driveActual
  const driveExpected = sidebarData.kpis.driveExpected
  const driveVariance = driveActual - driveExpected
  const scaleMax = Math.max(total, onSite + driveExpected, 1) * 1.05
  const onSitePct = (onSite / scaleMax) * 100
  const drivePct = (driveActual / scaleMax) * 100
  const expectedEndPct = ((onSite + driveExpected) / scaleMax) * 100

  return (
    <DetailCard classes={classes} title="Crew time per visit" subtitle="actual · incl. drive">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 24, fontWeight: 800, color: V4_T.ink, lineHeight: 1 }}>{formatNumber(total, 2)}h</span>
        <span style={{ fontSize: 11.5, color: V4_T.mute, whiteSpace: 'nowrap' }}>
          avg total · <span style={{ color: V4_T.green, fontWeight: 700, fontFamily: V4_T.mono }}>{formatNumber(onSite, 2)}</span> on-site + <span style={{ color: V4_T.amber, fontWeight: 700, fontFamily: V4_T.mono }}>{formatNumber(driveActual, 2)}</span> drive
        </span>
      </div>
      <div style={{ position: 'relative', height: 28, background: V4_T.cream, borderRadius: 6, overflow: 'hidden', border: `1px solid ${V4_T.lineSoft}` }}>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${clamp(onSitePct, 0, 100)}%`, background: V4_T.green }} />
        <div style={{ position: 'absolute', left: `${clamp(onSitePct, 0, 100)}%`, top: 0, bottom: 0, width: `${clamp(drivePct, 0, 100)}%`, background: driveVariance > 0 ? V4_T.rust : V4_T.amber, opacity: 0.92 }} />
        <div style={{ position: 'absolute', left: `calc(${clamp(expectedEndPct, 0, 100)}% - 1px)`, top: -3, bottom: -3, width: 2, background: V4_T.ink, borderRadius: 1 }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
        <MiniBreakdown label="On-site" value={`${formatNumber(onSite, 2)}h`} sub="productive work" color={V4_T.green} />
        <MiniBreakdown label="Drive" value={`${formatNumber(driveActual, 2)}h`} sub={`vs ${formatNumber(driveExpected, 2)}h expected`} color={driveVariance > 0 ? V4_T.rust : V4_T.amber} delta={`${driveVariance > 0 ? '+' : ''}${formatNumber(driveVariance, 2)}`} />
      </div>
      {driveVariance > 0 && (
        <div style={{ marginTop: 10, padding: '9px 11px', background: V4_T.rustSoft, border: '1px solid #e8c4a8', borderRadius: 7, fontSize: 11.5, color: '#7a3115', lineHeight: 1.45 }}>
          Driving runs <strong>{formatNumber(driveVariance, 2)}h over expected per visit</strong> - about <strong>{formatNumber(driveVariance * sidebarData.kpis.completedVisits, 1)} extra hours</strong> across {sidebarData.kpis.completedVisits} completed visits.
        </div>
      )}
    </DetailCard>
  )
}

function MiniBreakdown ({ label, value, sub, color, delta }) {
  return (
    <div style={{ padding: '8px 10px', borderRadius: 7, background: V4_T.surface2, border: `1px solid ${V4_T.lineSoft}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
        <span style={{ width: 7, height: 7, borderRadius: 2, background: color }} />
        <span style={{ fontSize: 9, fontWeight: 800, color: V4_T.muteSoft, textTransform: 'uppercase' }}>{label}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: V4_T.ink, fontFamily: V4_T.mono, lineHeight: 1.1 }}>{value}</span>
        {delta && <span style={{ fontSize: 10, fontWeight: 700, color, fontFamily: V4_T.mono }}>{delta}</span>}
      </div>
      <div style={{ fontSize: 10.5, color: V4_T.muteSoft, marginTop: 2 }}>{sub}</div>
    </div>
  )
}

function HistoryTab ({ classes, sidebarData, detailHistoryRange, setDetailHistoryRange, detailHistory, hoveredVisit, setHoveredVisit, pinnedVisit, setPinnedVisit, visitTypeFilter, setVisitTypeFilter }) {
  const visibleVisitRows = filterVisitRowsByType(sidebarData.visitRows, visitTypeFilter)
  const visibleTrend = getVisitTrendStats(visibleVisitRows, sidebarData.kpis.avgHours)
  const activeVisit = pinnedVisit || hoveredVisit
  const VISIT_LOG_PAGE = 12
  const [visitLogVisible, setVisitLogVisible] = useState(VISIT_LOG_PAGE)
  // Reset pagination when the underlying data set or active filter changes.
  useEffect(() => { setVisitLogVisible(VISIT_LOG_PAGE) }, [sidebarData.visitRows, visitTypeFilter])
  const previewVisit = (visit) => {
    if (!pinnedVisit) setHoveredVisit(visit)
  }
  const openVisit = (visit) => {
    const visitUrl = getAspireWorkTicketVisitUrl(visit) || getAspireWorkTicketUrl(visit)
    if (visitUrl) window.open(visitUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <>
      <DetailCard
        classes={classes}
        title="Visit timeline"
        subtitle={`${formatNumber(visibleTrend.recentAvg, 1)}h recent avg`}
        right={
          <SegmentedControl
            value={detailHistoryRange}
            options={[{ value: '30', label: '30d' }, { value: '90', label: '90d' }, { value: 'year', label: '1y' }]}
            onChange={setDetailHistoryRange}
          />
        }
      >
        {detailHistory.error && <div className={classes.emptyDrawerNote} style={{ marginBottom: 8 }}>Could not load synced work-ticket visit history: {detailHistory.error}</div>}
        <div style={{ background: V4_T.cream, borderRadius: 8, padding: '6px 2px 2px', margin: '0 -3px' }}>
          <HoursDotPlot
            visits={visibleVisitRows}
            est={sidebarData.kpis.estHoursPerVisit}
            onHover={previewVisit}
            onSelect={setPinnedVisit}
            onOpen={openVisit}
            height={134}
          />
        </div>
        <VisitLegend value={visitTypeFilter} onChange={(nextFilter) => {
          setVisitTypeFilter(nextFilter)
          setHoveredVisit(null)
          setPinnedVisit(null)
        }} />
        <div style={{ marginTop: 6, fontSize: 10.5, fontWeight: 700, color: visibleTrend.trendPct > 0 ? V4_T.rust : V4_T.green, textAlign: 'right' }}>
          {visibleTrend.trendPct > 0 ? '+' : ''}{visibleTrend.trendPct}% vs prior 8wk
        </div>
        {activeVisit && <HoverStrip visit={activeVisit} pinned={!!pinnedVisit} onClear={() => {
          setPinnedVisit(null)
          setHoveredVisit(null)
        }} />}
      </DetailCard>

      <DetailCard
        classes={classes}
        title="Visit log"
        subtitle={visitTypeFilter === 'all'
          ? `${sidebarData.kpis.completedVisits} completed · 12mo`
          : `${visibleVisitRows.length} of ${sidebarData.visitRows.length} · ${visitTypeFilter === 'Maint' ? 'maintenance' : visitTypeFilter === 'Irrig' ? 'irrigation' : visitTypeFilter === 'Herb' ? 'herbicide' : visitTypeFilter}`}
        action={detailHistory.loading ? 'Loading' : 'Export'}
      >
        {visibleVisitRows.slice(0, visitLogVisible).map((visit, index) => {
          const color = SERVICE_COLORS[visit.type] || V4_T.mute
          const serviceLabel = visit.label || { Maint: 'Weekly maintenance', Irrig: 'Irrigation service', Herb: 'Herbicide application', Other: 'Service visit' }[visit.type]
          const ticketLabel = getTicketLabel(visit)
          const ticketUrl = getAspireWorkTicketUrl(visit) || getAspireWorkTicketVisitUrl(visit)
          const { chips, fallback: peopleFallback, overflow } = getVisitPeopleChips(visit)
          const visitOT = getVisitOT(visit)
          const timeRange = getVisitTimeRange(visit)
          const timeEntryUrl = getAspireTimeEntryUrl(visit)
          const driveHours = getVisitAssociatedDriveHours(visit)
          return (
            <div key={visit.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: index < Math.min(visibleVisitRows.length, visitLogVisible) - 1 ? `1px solid ${V4_T.lineSoft}` : 'none' }}>
              <span style={{ width: 3, alignSelf: 'stretch', background: color, borderRadius: 2, opacity: visit.flag === 'high' ? 1 : 0.7, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: V4_T.ink, fontFamily: V4_T.mono }}>{visit.date}</span>
                  {visit.flag === 'high' && <span style={{ fontSize: 9, fontWeight: 800, color: V4_T.rust, padding: '1px 5px', background: V4_T.rustSoft, borderRadius: 3 }}>OUTLIER</span>}
                </div>
                <div style={{ fontSize: 10.5, color: V4_T.mute, marginTop: 1, lineHeight: 1.4 }}>{serviceLabel}</div>
                {chips.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4 }}>
                    {chips.map(chip => (
                      <div key={chip.name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            background: chip.color,
                            color: '#fff',
                            fontSize: 8.5,
                            fontWeight: 800,
                            fontFamily: V4_T.mono,
                            letterSpacing: '-0.02em',
                            flexShrink: 0
                          }}
                        >
                          {chip.initials}
                        </span>
                        <span style={{ fontSize: 10.5, color: V4_T.ink2, lineHeight: 1.2 }}>{chip.name}</span>
                      </div>
                    ))}
                    {overflow > 0 && (
                      <div style={{ fontSize: 10, color: V4_T.muteSoft, fontFamily: V4_T.mono, fontWeight: 700, marginLeft: 24 }}>+{overflow} more</div>
                    )}
                  </div>
                ) : peopleFallback ? (
                  <div style={{ fontSize: 10.5, color: V4_T.muteSoft, marginTop: 2 }}>{peopleFallback}</div>
                ) : null}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, gap: 2 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: V4_T.ink, fontFamily: V4_T.mono }}>{formatNumber(visit.hrs, 2)}h</div>
                {visit.isEstimated && (
                  <span style={{ fontSize: 8.5, fontWeight: 800, color: V4_T.amber, background: '#fff3d8', borderRadius: 3, padding: '1px 5px', letterSpacing: '0.04em' }}>
                    EST
                  </span>
                )}
                <div style={{ fontSize: 9.5, color: V4_T.green, fontFamily: V4_T.mono, fontWeight: 600 }}>
                  {ticketUrl && ticketLabel ? (
                    <MuiLink
                      href={ticketUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      style={{ color: V4_T.green, fontWeight: 800, fontFamily: V4_T.mono }}
                    >
                      {ticketLabel}
                    </MuiLink>
                  ) : (
                    ticketLabel || 'TX -'
                  )}
                </div>
                {timeRange && (
                  timeEntryUrl ? (
                    <MuiLink
                      href={timeEntryUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      style={{ fontSize: 11, color: V4_T.ink2, fontFamily: V4_T.mono, fontWeight: 600, marginTop: 2, whiteSpace: 'nowrap', textDecoration: 'underline', textDecorationColor: V4_T.lineSoft, textUnderlineOffset: 2 }}
                    >
                      {timeRange}
                    </MuiLink>
                  ) : (
                    <span style={{ fontSize: 11, color: V4_T.muteSoft, fontFamily: V4_T.mono, marginTop: 2, whiteSpace: 'nowrap' }}>{timeRange}</span>
                  )
                )}
                {visitOT.otHours > 0 && (
                  <span style={{ fontSize: 10, color: V4_T.rust, fontFamily: V4_T.mono, fontWeight: 700, marginTop: 1, whiteSpace: 'nowrap' }}>
                    {formatNumber(visitOT.otHours, 1)}h OT{visitOT.otCost > 0 ? ` · ${formatCurrency(visitOT.otCost)}` : ''}
                  </span>
                )}
                {driveHours > 0 && (
                  <span
                    title="HoursAct − OnSiteHours"
                    style={{ fontSize: 10, color: V4_T.muteSoft, fontFamily: V4_T.mono, marginTop: 1, whiteSpace: 'nowrap' }}
                  >
                    +{formatNumber(driveHours, 2)}h drive
                  </span>
                )}
              </div>
            </div>
          )
        })}
        {visibleVisitRows.length > VISIT_LOG_PAGE && (
          <div style={{ fontSize: 10, color: V4_T.muteSoft, marginTop: 10, textAlign: 'center', fontFamily: V4_T.mono }}>
            SHOWING {Math.min(visitLogVisible, visibleVisitRows.length)} OF {visibleVisitRows.length}
            {visitLogVisible < visibleVisitRows.length ? (
              <>
                {' · '}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => setVisitLogVisible(v => Math.min(v + VISIT_LOG_PAGE, visibleVisitRows.length))}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setVisitLogVisible(v => Math.min(v + VISIT_LOG_PAGE, visibleVisitRows.length)) }}
                  style={{ color: V4_T.green, fontWeight: 700, cursor: 'pointer' }}
                >
                  LOAD MORE →
                </span>
              </>
            ) : (
              <>
                {' · '}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => setVisitLogVisible(VISIT_LOG_PAGE)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setVisitLogVisible(VISIT_LOG_PAGE) }}
                  style={{ color: V4_T.green, fontWeight: 700, cursor: 'pointer' }}
                >
                  SHOW LESS ↑
                </span>
              </>
            )}
          </div>
        )}
        {visibleVisitRows.length === 0 && (
          <div style={{ fontSize: 11, color: V4_T.muteSoft, marginTop: 8, textAlign: 'center', fontStyle: 'italic' }}>
            No visits match the current filter.
          </div>
        )}
      </DetailCard>
    </>
  )
}

function ServiceTab ({ classes, sidebarData, dominantService }) {
  return (
    <>
      <DetailCard classes={classes} title="Service mix" subtitle="this year · by hours">
        <ServiceBars services={sidebarData.serviceRows} />
        <div style={{ marginTop: 12, padding: '9px 11px', background: V4_T.surface2, borderRadius: 7, border: `1px solid ${V4_T.lineSoft}`, fontSize: 11.5, color: V4_T.ink2, lineHeight: 1.45 }}>
          {dominantService ? <><strong style={{ color: V4_T.ink }}>{dominantService.name}</strong> is the largest service share at {Math.round(dominantService.share * 100)}% of tracked hours.</> : 'Service-level hours are not available for this property yet.'}
        </div>
      </DetailCard>
      {sidebarData.serviceRows.map(service => {
        const color = SERVICE_COLORS[service.name] || V4_T.mute
        const completion = service.tickets ? service.completed / service.tickets : 0
        return (
          <DetailCard key={service.name} classes={classes} title={service.name} subtitle="details">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 4 }}>
              {[
                ['Tickets', service.tickets],
                ['Done', service.completed],
                ['Future', service.future],
                ['Hours', formatNumber(service.hours, 1)],
                ['$ Cost', formatCurrencyOrPending(service.cost)]
              ].map(([label, value]) => (
                <div key={label} style={{ textAlign: 'center', padding: '6px 0', minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: V4_T.ink, fontFamily: V4_T.mono, lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
                  <div style={{ fontSize: 9, color: V4_T.muteSoft, fontWeight: 700, marginTop: 5, textTransform: 'uppercase' }}>{label}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10 }}>
              <div style={{ height: 5, background: V4_T.cream, borderRadius: 3, overflow: 'hidden', border: `1px solid ${V4_T.lineSoft}` }}>
                <div style={{ width: `${clamp(completion * 100, 0, 100)}%`, height: '100%', background: color }} />
              </div>
              <div style={{ fontSize: 10.5, color: V4_T.mute, marginTop: 4 }}>{Math.round(completion * 100)}% complete · {service.future} remaining</div>
            </div>
          </DetailCard>
        )
      })}
    </>
  )
}

const defaultRenderTicketLink = (visit) => {
  const ticketLabel = getTicketLabel(visit)
  const ticketUrl = getAspireWorkTicketUrl(visit)
  if (!ticketLabel) return null
  if (!ticketUrl) return ticketLabel
  return (
    <MuiLink
      href={ticketUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
      style={{ fontWeight: 700 }}
    >
      {ticketLabel}
    </MuiLink>
  )
}

function ScheduleTab ({ classes, selectedProperty, sidebarData, dayAbbrev, renderTicketLink = defaultRenderTicketLink }) {
  const upcomingVisits = sidebarData.operationalUpcomingVisits || selectedProperty?.futureVisits || []
  const splitGroups = sidebarData.splitUpcomingVisitGroups || []
  const nextSplit = splitGroups[0] || null
  const prescribedManHours = Number(selectedProperty?.prescribedManHours || 0)
  const formatVisitHours = (visit) => {
    if (prescribedManHours > 0) {
      const siblingRows = upcomingVisits.filter(item => String(item.workTicketId || item.workTicketNumber) === String(visit.workTicketId || visit.workTicketNumber))
      const siblingHours = siblingRows.reduce((sum, item) => sum + Math.max(0, Number(item.estimatedManHours || 0)), 0)
      const share = siblingRows.length > 1
        ? (siblingHours > 0 ? Math.max(0, Number(visit.estimatedManHours || 0)) / siblingHours : 1 / siblingRows.length)
        : 1
      return `${formatNumber(prescribedManHours * share, 1)} planned mh`
    }
    if (Number(visit.estimatedManHours || 0) > 0) return `${formatNumber(visit.estimatedManHours, 1)} mh`
    return `${formatNumber(visit.estimatedHours, 2)}h`
  }
  return (
    <>
      {nextSplit && (
        <DetailCard classes={classes} title="Split service" subtitle={`${nextSplit.splitCount} crew visits on one work ticket`}>
          <div style={{ padding: '9px 11px', borderRadius: 8, background: '#fff4df', border: '1px solid #e7c988' }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: V4_T.ink }}>
              Ticket {nextSplit.rows[0]?.workTicketNumber || nextSplit.ticketKey} needs {nextSplit.splitCount} visits
            </div>
            <div style={{ fontSize: 10.5, color: V4_T.mute, marginTop: 3 }}>
              Each crew visit is scheduled and managed independently.
            </div>
            <div style={{ marginTop: 7, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {nextSplit.rows.map((visit, index) => (
                <div key={visit.workTicketVisitId || index} style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 10.5 }}>
                  <span style={{ minWidth: 28, padding: '1px 5px', borderRadius: 9, textAlign: 'center', background: '#e7ecff', color: '#3454c1', fontWeight: 800 }}>{visit.splitIndex || index + 1}/{visit.splitCount || nextSplit.splitCount}</span>
                  <span style={{ color: V4_T.ink2, fontWeight: 700 }}>{visit.routeName || visit.crewLeaderName || 'Crew pending'}</span>
                </div>
              ))}
            </div>
          </div>
        </DetailCard>
      )}
      <DetailCard classes={classes} title="Cadence" subtitle={`${sidebarData.scheduledDay || 'unscheduled'} recurrence`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 9, background: V4_T.greenSoft, border: '1px solid #c7dac8', marginBottom: 11 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '5px 11px', borderRadius: 7, background: V4_T.green, color: V4_T.surface, flexShrink: 0 }}>
            <span style={{ fontSize: 9.5, fontWeight: 700 }}>EVERY</span>
            <span style={{ fontSize: 15, fontWeight: 800 }}>{dayAbbrev}</span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: V4_T.ink }}>{sidebarData.crewName || 'Crew pending'}</div>
            <div style={{ fontSize: 11.5, color: V4_T.mute, marginTop: 2 }}>Maintenance · active recurrence</div>
            <div style={{ fontSize: 10.5, color: V4_T.muteSoft, marginTop: 4, fontFamily: V4_T.mono }}>{sidebarData.kpis.completedVisits} DONE · {sidebarData.kpis.futureVisits} AHEAD</div>
          </div>
        </div>
        <div style={{ fontSize: 9.5, fontWeight: 800, color: V4_T.muteSoft, textTransform: 'uppercase', marginBottom: 6 }}>Recurrence</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => {
            const active = String(sidebarData.scheduledDay || '').toLowerCase() === day.toLowerCase()
            return (
              <div key={day} style={{ aspectRatio: '1/1', borderRadius: 6, background: active ? V4_T.green : V4_T.cream, border: `1px solid ${active ? V4_T.green : V4_T.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: active ? V4_T.surface : V4_T.muteSoft, fontFamily: V4_T.mono }}>
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'][index]}
              </div>
            )
          })}
        </div>
      </DetailCard>

      <DetailCard
        classes={classes}
        title="Upcoming visits"
        subtitle={sidebarData.kpis.operationalVisitCount !== sidebarData.kpis.futureVisits
          ? `${sidebarData.kpis.futureVisits} tickets · ${sidebarData.kpis.operationalVisitCount} crew visits`
          : `${sidebarData.kpis.futureVisits} scheduled`}
        action="Export"
      >
        {upcomingVisits.slice(0, 8).map((visit, index) => {
          const date = visit.date ? new Date(visit.date) : null
          const validDate = date && !isNaN(date.getTime())
          return (
            <div key={`${visit.workTicketVisitId || visit.workTicketId || visit.workTicketNumber || index}`} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 0', borderBottom: index < Math.min(upcomingVisits.length, 8) - 1 ? `1px solid ${V4_T.lineSoft}` : 'none' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 36, padding: '4px 0', background: V4_T.cream, borderRadius: 5, border: `1px solid ${V4_T.line}` }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: V4_T.muteSoft, textTransform: 'uppercase' }}>{getDayAbbrev(visit.dayOfWeek || (validDate ? date.toLocaleString([], { weekday: 'short' }) : ''))}</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: V4_T.ink, lineHeight: 1.05, fontFamily: V4_T.mono }}>{validDate ? date.getDate() : '-'}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: V4_T.ink }}>{validDate ? date.toLocaleDateString() : 'Date pending'}</span>
                  {visit.splitCount > 1 && <span style={{ padding: '1px 5px', borderRadius: 8, background: '#e7ecff', color: '#3454c1', fontSize: 9, fontWeight: 800 }}>{visit.splitIndex}/{visit.splitCount}</span>}
                </div>
                <div style={{ fontSize: 10.5, color: V4_T.mute, marginTop: 1 }}>{visit.routeName || visit.crewLeaderName || sidebarData.crewName || 'Crew pending'}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: V4_T.ink, fontFamily: V4_T.mono }}>{formatVisitHours(visit)}</div>
                <div style={{ fontSize: 9.5, color: V4_T.green, fontFamily: V4_T.mono, fontWeight: 600, marginTop: 1 }}>{renderTicketLink(visit)}</div>
              </div>
            </div>
          )
        })}
        {upcomingVisits.length === 0 && <div className={classes.emptyDrawerNote}>No upcoming visits are attached to this property yet.</div>}
      </DetailCard>
    </>
  )
}

function FinancialsTab ({ classes, sidebarData }) {
  const [period, setPeriod] = useState('ytd')
  const periodData = useMemo(() => buildFinancialPeriodData(sidebarData, period), [sidebarData, period])
  const kpis = periodData.kpis
  const periodMeta = periodData.meta
  const billingGroup = sidebarData.billingGroup
  const memberCount = billingGroup && Array.isArray(billingGroup.members) ? billingGroup.members.length : 0
  return (
    <>
      {billingGroup && (
        <div style={{ background: V4_T.greenSoft, border: `1px solid ${V4_T.green}55`, borderRadius: 8, padding: '8px 12px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: V4_T.green, flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: V4_T.ink2 }}>
            <strong style={{ color: V4_T.green }}>Billing group: {billingGroup.name}</strong>
            {memberCount ? ` — combined revenue & labor across ${memberCount} properties` : ''}
          </span>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 800, color: V4_T.muteSoft, textTransform: 'uppercase', letterSpacing: 0.4 }}>Scope</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: V4_T.ink2 }}>
          All services · {periodMeta.detailLabel}
        </span>
      </div>
      <DetailCard
        classes={classes}
        title="Money in / out"
        subtitle={periodMeta.chartLabel}
        right={<SegmentedControl value={period} options={FINANCIAL_PERIOD_OPTIONS} onChange={setPeriod} />}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: V4_T.ink, lineHeight: 1 }}>{formatCompactCurrency(kpis.totalRev)}</span>
          <span style={{ fontSize: 10.5, color: V4_T.mute, whiteSpace: 'nowrap' }}>earned revenue · {periodMeta.shortLabel}</span>
          <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: kpis.totalRev - kpis.actualLabor >= 0 ? V4_T.green : V4_T.rust, fontFamily: V4_T.mono, whiteSpace: 'nowrap' }}>Δ {formatCurrency(kpis.totalRev - kpis.actualLabor)}</span>
        </div>
        <div style={{ background: V4_T.cream, borderRadius: 8, padding: '8px 4px 2px', margin: '0 -3px' }}>
          <RevCostBars monthly={periodData.monthlyRows} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6, marginTop: 9 }}>
          <MoneyLegend color={V4_T.green} label="Maintenance" value={formatCurrency(kpis.earnedRev)} />
          <MoneyLegend color={V4_T.amber} label="Other rev" value={formatCurrency(kpis.othRev)} sub="non-maint earned" />
          <MoneyLegend color={V4_T.mute} label="Labor cost" value={formatCurrency(kpis.actualLabor)} />
        </div>
      </DetailCard>

      <DetailCard classes={classes} title="Financials" subtitle={periodMeta.detailLabel}>
        {[
          ['Maintenance revenue', formatCurrency(kpis.earnedRev), 'good', null],
          ['Other revenue', formatCurrency(kpis.othRev), 'other', 'non-maint earned'],
          ['Total earned revenue', formatCurrency(kpis.totalRev), 'bold', null],
          ['Estimated labor', formatCurrency(kpis.estLabor), null, 'should-be'],
          ['Actual labor', formatCurrency(kpis.actualLabor), kpis.costVariance >= 0 ? 'good' : 'bad', 'all services'],
          ['Net margin', formatPercent(kpis.margin), kpis.margin < 10 ? 'bad' : 'good', `${formatCurrency(kpis.netProfit)} net`]
        ].map((row, index) => (
          <div key={row[0]} style={{ display: 'flex', alignItems: 'center', padding: '9px 2px', borderTop: index > 0 ? `1px solid ${V4_T.lineSoft}` : 'none', background: row[2] === 'bold' ? V4_T.surface2 : 'transparent', marginLeft: row[2] === 'bold' ? -8 : 0, marginRight: row[2] === 'bold' ? -8 : 0, paddingLeft: row[2] === 'bold' ? 10 : 2, paddingRight: row[2] === 'bold' ? 10 : 2 }}>
            <span style={{ flex: 1, fontSize: 12, color: row[2] === 'bold' ? V4_T.ink : V4_T.ink2, fontWeight: row[2] === 'bold' ? 700 : 400 }}>{row[0]}</span>
            {row[3] && <span style={{ fontSize: 10.5, color: V4_T.muteSoft, marginRight: 10, fontFamily: V4_T.mono }}>{row[3]}</span>}
            <span style={{ fontSize: 13.5, fontWeight: 800, color: row[2] === 'good' ? V4_T.green : row[2] === 'bad' ? V4_T.rust : row[2] === 'other' ? '#a87320' : V4_T.ink, fontFamily: V4_T.mono }}>{row[1]}</span>
          </div>
        ))}
      </DetailCard>
    </>
  )
}

function MoneyLegend ({ color, label, value, sub }) {
  return (
    <div style={{ padding: '7px 9px', borderRadius: 7, background: V4_T.surface2, border: `1px solid ${V4_T.lineSoft}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
        <span style={{ width: 7, height: 7, borderRadius: 2, background: color, flexShrink: 0 }} />
        <span style={{ fontSize: 9, fontWeight: 800, color: V4_T.muteSoft, textTransform: 'uppercase', letterSpacing: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: V4_T.ink, fontFamily: V4_T.mono, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 9.5, color: V4_T.muteSoft, marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

export default PropertyDetailDrawer
